import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import { pick, get } from "lodash";
import searchSchema from "store/schema/search";
import { createAPIAction } from "store/actions/api";
import { searchV2FilterOptions, defaultSort } from "config/filters";
import navigate from "utils/navigate";
import { buildSearchFilterLink } from "utils/search/buildSearchLink";
import buildLandingQuery from "utils/search/buildLandingQuery";

// =============================================================================
// INITIAL STATE
// =============================================================================

export const initialState = {
	loading: false,
	locationKey: null,
	error: null,
	statusCode: null,
	facetCounts: {},
	facilities: [],
	listings: null,
	nearbyFacilities: [],
	searchFilters: searchV2FilterOptions,
	sort: defaultSort,
	cityUrl: null,
	stateUrl: null,
	query: {},
	content: {},
	meta: {},
	location: {},
	pageType: null,
	// When backend returns lowPerformingAlt, redirect user to alternate city/state
	redirectTo: null
};

// =============================================================================
// REDUCER
// =============================================================================

const reducers = {
	activateSearchFilterItem: (state, action) => {
		const { payload } = action;
		const { name, value } = payload;
		// e.g. searchFilters.amenities.options.climateControlled
		// only try to check if the value exists
		if (state.searchFilters[name].options[value]) {
			state.searchFilters[name].options[value].checked = true;
		}
	},
	deactivateSearchFilterItem: (state, { payload }) => {
		const { name, value } = payload;
		// e.g. searchFilters.amenities.options.climateControlled
		if (state.searchFilters[name].options[value]) {
			state.searchFilters[name].options[value].checked = false;
		}
	},
	soloSearchFilterItem: (state, { payload }) => {
		// activates the provided option, deactivates all others

		// taken from the "solo" functionality in a DAW,
		// to only listen to one track and mute all the others
		const { name, value } = payload;
		const { options } = state.searchFilters[name];
		const updatedOptions = Object.keys(options).reduce((acc, key) => {
			acc[key] = {
				...options[key],
				checked: key === value
			};
			return acc;
		}, {});

		state.searchFilters[name].options = {
			...updatedOptions
		};
	},
	setSortOrder: (state, { payload }) => {
		state.sort = payload;
	},
	clearRedirectTo: (state) => {
		state.redirectTo = null;
	},
	clearSearchError: (state) => {
		state.error = null;
	}
};

// =============================================================================
// THUNKS
// =============================================================================

// TODO: check location key, ensures they're searching a new location?
// Results and search page meta (e.g. SEO data)
export const getSearchPage = createAPIAction({
	actionName: "search/getSearchPage",
	uri: "/api/search",
	schema: searchSchema,
	queryDefaults: {
		sort: defaultSort,
		excludeOon: true
	}
});

export const getLandingPage = (match, queryParams, landingType) => {
	const query = buildLandingQuery(match, queryParams, landingType);
	return createAPIAction({
		actionName: "search/getSearchPage",
		uri: "/api/search/landing",
		schema: searchSchema,
		queryDefaults: {
			sort: defaultSort,
			excludeOon: true
		}
	})({ query });
};

// A page of facilities (e.g. without SEO data)
export const getSearchResults = createAPIAction({
	actionName: "search/getSearchResults",
	uri: "/api/search/slim",
	schema: searchSchema,
	queryDefaults: {
		sort: defaultSort,
		excludeOon: true
	}
});

// =============================================================================
// EXTRA REDUCERS
// =============================================================================

const extraReducers = (builder) => {
	builder
		.addMatcher(isAnyOf(getSearchPage.pending), (state) => {
			state.loading = true;
			// Clear facilities so old search results don't show while new search loads
			state.facilities = [];
		})
		.addMatcher(isAnyOf(getSearchResults.pending), (state) => {
			state.loading = true;
		})
		.addMatcher(isAnyOf(getSearchPage.fulfilled), (state, action) => {
			const { entities, result } = action.payload;
			const search = entities.search[result];

			// Backend suggests alternate city/state when area has few/no facilities; client navigates
			const alternateCity = get(
				search,
				"content.lowPerformingAlt.alternateCity"
			);
			const alternateState = get(
				search,
				"content.lowPerformingAlt.alternateState"
			);
			if (alternateCity && alternateState) {
				state.redirectTo = `/self-storage/${alternateState}/${alternateCity}/`;
			}

			// the query as returned by the API, not the params
			state.query = search.query;

			// 1:1
			state.searchType = search.searchType;
			state.facetCounts = search.facetCounts;
			state.cityUrl = search.cityUrl;
			state.stateUrl = search.stateUrl;
			// Only update location when API returned valid location (avoids undefined/ocean when map goes out).
			// Preserve previous lat/lng if API omits them so the "no search if map moved < 2 miles" check still works.
			const hasValidLocation =
				search.location &&
				((typeof search.location.latitude === "number" &&
					typeof search.location.longitude === "number") ||
					(search.location.city && search.location.state));
			if (hasValidLocation) {
				const hasNewCoords =
					typeof search.location.latitude === "number" &&
					typeof search.location.longitude === "number";
				state.location = {
					...search.location,
					preferredPlaceName:
						search.location?.subLocality || search.location?.city,
					// Keep previous map center when API doesn't return coords (2-mile threshold needs them)
					...(hasNewCoords
						? {}
						: {
								latitude: state.location?.latitude,
								longitude: state.location?.longitude
							})
				};
			}
			state.locationHash = search.locationHash;
			state.meta = get(search, "meta", {});
			state.pageText = get(search.content, "pageText", null);
			state.nearbyCities = get(search.content, "nearbyCities", null);
			state.amenityPages = get(
				search.content,
				"cityLinks.amenities",
				null
			);
			state.headline = search.content?.meta?.headline;
			state.staticMapUrl = search.staticMapUrl;
			state.totalPages = search.totalPages;
			state.totalListings = search.totalListings;
			state.listingsPerPage = search.listingsPerPage;
			state.lastInOriginalSearch = search.lastInOriginalSearch;

			// remaps
			state.pageType = search.query?.pageType;
			state.subPageType = search.query?.subpageType || state.pageType;
			// state.filtersShowing = false;
			state.content = search.content; // old version reverts to state.content if not SEARCH_SUCCESS
			// state.vipAccounts = VIP_ACCOUNTS
		})
		.addMatcher(isAnyOf(getSearchPage.fulfilled), (state, action) => {
			// Initialize the search filter state based on the query
			const { entities, result } = action.payload;
			const search = entities.search[result];

			// Reset all filters to unchecked before applying from API response
			Object.keys(state.searchFilters).forEach((groupKey) => {
				Object.keys(state.searchFilters[groupKey].options).forEach(
					(optionKey) => {
						state.searchFilters[groupKey].options[
							optionKey
						].checked = false;
					}
				);
			});

			Object.entries(
				pick(search.query, Object.keys(state.searchFilters))
			).forEach(([filterGroup, activeFilters]) => {
				[activeFilters].flat().forEach((filter) => {
					state.searchFilters[filterGroup].options[filter].checked =
						true;
				});
			});
			// initialize sort state
			state.sort = get(search.query, "sort", defaultSort);
		})
		// Full search (e.g. new location from map move): replace facilities
		.addMatcher(isAnyOf(getSearchPage.fulfilled), (state, action) => {
			const { entities, result } = action.payload;
			if (result) {
				const search = entities.search[result];
				state.searchId = search.searchId;
				state.facilities = Array.isArray(search.listings)
					? search.listings
					: [];
				state.page = search.page;
				state.hasMorePages = search.page !== search.totalPages;
			} else {
				// Malformed or empty response: clear facilities so old results don't persist
				state.facilities = [];
			}
			state.error = null;
			state.loading = false;
		})
		// Next page (load more): append facilities
		.addMatcher(isAnyOf(getSearchResults.fulfilled), (state, action) => {
			const { entities, result } = action.payload;
			if (result) {
				const search = entities.search[result];
				state.searchId = search.searchId;
				const appendListings = Array.isArray(search.listings)
					? search.listings
					: [];
				state.facilities = [...state.facilities, ...appendListings];
				state.page = search.page;
				state.hasMorePages = search.page !== search.totalPages;
			}
			state.error = null;
			state.loading = false;
		})
		.addMatcher(
			isAnyOf(getSearchPage.rejected, getSearchResults.rejected),
			(state) => {
				// handle redirecting to error page
				state.error = true;
				state.loading = false;
			}
		);
};

// =============================================================================
// SLICE
// =============================================================================

export const searchSlice = createSlice({
	name: "search",
	initialState,
	reducers,
	extraReducers
});

// =============================================================================
// SELECTORS
// =============================================================================

export const selectCheckedOptionsByGroup = (state, groupName) => {
	const options = Object.values(state.search.searchFilters[groupName].options)
		// we can filter through just those values for whichever is checked
		.filter((option) => option.checked)
		// and just return a string of the option value, e.g. 'climateControlled'
		.map(({ value }) => value);

	const allowMultiple = state.search.searchFilters[groupName].multiple;
	// if this is a 'multiple' group, we need to return an array
	// otherwise, there should only be one element, so we return a string
	return allowMultiple ? options : options.join();
};

export const selectSearchFilterParams = (state) =>
	Object
		// Returns an object we can encode in a url of just the checked options

		// get the filter group keys as an array
		.keys(state.search.searchFilters)
		// reduce that array to an object with each filter group key as a key,
		// and the value as an array of the checked options from that group
		// e.g. {'amenties' :['climateControlled']}
		.reduce(
			(acc, cur) => ({
				...acc,
				// options is an object with the options' values as keys
				// e.g. {'climateControlled: {'checked': false, 'value': 'climateControlled'}}
				[cur]: selectCheckedOptionsByGroup(state, cur)
			}),
			{}
		);

export const {
	soloSearchFilterItem,
	activateSearchFilterItem,
	deactivateSearchFilterItem,
	setSortOrder,
	setNextPage,
	clearRedirectTo,
	clearSearchError
} = searchSlice.actions;

export const selectSortParam = (state) => ({
	sort: state.search.sort
});

/**
 * Builds the same params object used when navigating to a filtered search page
 * (e.g. when user clicks Apply Filters). Use this so map move and other flows
 * produce URLs consistent with applySearch.
 * @param {Object} state - Redux state
 * @param {Object} baseParams - Base query params (e.g. from URL)
 * @param {Object} [overrides={}] - Params to override (e.g. location, city, state, page)
 * @returns {Object} Params ready for buildSearchFilterLink(pathname, params)
 */
export const buildFilteredSearchParams = (
	state,
	baseParams,
	overrides = {}
) => {
	const filterParams = selectSearchFilterParams(state);
	const sortParam = selectSortParam(state);
	const persistedQuery = state.search.query || {};
	const persistedDistance =
		persistedQuery.distance || persistedQuery.maxDistance;
	const hasDistanceInOverrides =
		overrides.distance !== undefined || baseParams.distance;
	const distanceParam =
		persistedDistance && !hasDistanceInOverrides
			? { distance: persistedDistance }
			: {};

	return {
		...baseParams,
		...sortParam,
		...filterParams,
		...distanceParam,
		...overrides
	};
};

export const applySearch = (pathname, queryParams) => (dispatch, getState) => {
	const state = getState();
	const params = buildFilteredSearchParams(state, queryParams);
	const newUrl = buildSearchFilterLink(pathname, params);
	navigate(`${newUrl}#searchPage`);
};

// Client-side search apply used by the new SearchPageV2 filters.
// Builds the same query as applySearch, but instead of triggering a full
// page reload it:
// 1) updates the URL using the History API (when available), and
// 2) dispatches getSearchPage to refresh results and search state.
// Optional pushUrl (e.g. history.push from React Router) keeps router location in sync.
export const applySearchClient =
	(pathname, queryParams, pushUrl) => (dispatch, getState) => {
		const state = getState();
		const filterParams = selectSearchFilterParams(state);
		const sortParam = selectSortParam(state);

		const persistedQuery = state.search.query || {};
		const persistedDistance =
			persistedQuery.distance || persistedQuery.maxDistance;
		const distanceParam =
			persistedDistance && !queryParams.distance
				? { distance: persistedDistance }
				: {};

		// Use the last effective search query as the base so we
		// preserve location and other server-provided params, then
		// overlay filters/sort and reset to page 1 for a fresh search.
		const hasSearchQuery =
			persistedQuery && Object.keys(persistedQuery).length > 0;
		const baseQuery = hasSearchQuery ? persistedQuery : queryParams;

		const nextQuery = {
			...baseQuery,
			page: 1,
			...sortParam,
			...filterParams,
			...distanceParam
		};

		const newUrl = buildSearchFilterLink(pathname, nextQuery);
		const urlWithHash = `${newUrl}#searchPage`;

		// Use React Router's push when provided so location stays in sync (avoids
		// URL/state desync on browser Back when pushState was used).
		if (typeof pushUrl === "function") {
			pushUrl(urlWithHash);
		} else if (typeof globalThis.window !== "undefined" && globalThis.history?.pushState) {
			globalThis.history.pushState({}, "", urlWithHash);
		} else {
			// Fallback for very old browsers: preserve existing behavior.
			navigate(urlWithHash);
		}

		return dispatch(
			getSearchPage({
				query: nextQuery
			})
		);
	};

export const toggleOnSearchFilter =
	({ name, value }) =>
	(dispatch, getState) => {
		// Checks to see if filter group allows mutliple, then either activates or solos the item
		const { multiple } = getState().search.searchFilters[name];
		if (multiple) dispatch(activateSearchFilterItem({ name, value }));
		else dispatch(soloSearchFilterItem({ name, value }));
	};

export const getNextSearchResultsPage = (queryParams) =>
	function loadNextFacilities(dispatch, getState) {
		const state = getState();
		const filterParams = selectSearchFilterParams(state);
		const sortParams = selectSortParam(state);
		const nextPageQueryParams = {
			...queryParams,
			page: queryParams.page + 1
		};

		// Persist effective distance and listingsPerPage from initial search response
		const persistedQuery = state.search.query || {};
		const persistedDistance =
			persistedQuery.distance || persistedQuery.maxDistance;
		if (persistedDistance && !nextPageQueryParams.distance) {
			nextPageQueryParams.distance = persistedDistance;
		}

		dispatch(
			getSearchResults({
				query: {
					...nextPageQueryParams,
					...sortParams,
					...filterParams
				}
			})
		);
	};

/**
 * Push URL for next page to history (so back button returns to previous page) and load next page.
 * Used when same-city map move triggers pagination.
 */
export const pushSearchNextPageFromMapMove =
	({ pathname, queryParams, currentPage, pushUrl }) =>
	(dispatch, getState) => {
		const state = getState();
		const nextPage = (currentPage ?? 1) + 1;
		const params = buildFilteredSearchParams(state, queryParams, {
			page: nextPage
		});
		delete params.pageType;
		pushUrl(`${buildSearchFilterLink(pathname, params)}#searchPage`);
		dispatch(
			getNextSearchResultsPage({ ...queryParams, page: currentPage ?? 1 })
		);
	};

/**
 * Fetch new facility list from map center without changing the URL.
 * Sends raw lat/lng; prefer fetchSearchFromMapCenterWithLocation when you have
 * city/state (e.g. after reverse geocode) so search does not fail over ocean/unserved areas.
 */
export const fetchSearchFromMapCenter = (latitude, longitude) =>
	function fetchFromMapCenter(dispatch, getState) {
		const state = getState();
		const filterParams = selectSearchFilterParams(state);
		const sortParam = selectSortParam(state);
		const persistedQuery = state.search.query || {};
		const persistedDistance =
			persistedQuery.distance || persistedQuery.maxDistance;
		const distanceParam = persistedDistance
			? { distance: persistedDistance }
			: {};

		dispatch(
			getSearchPage({
				query: {
					location: `${latitude}, ${longitude}`,
					page: 1,
					...sortParam,
					...filterParams,
					...distanceParam
				}
			})
		);
	};

/**
 * Fetch new facility list using resolved city/state (e.g. from reverse geocode on map move).
 * Keeps search valid when map is over ocean or unserved areas by using nearest city/state.
 * Pass latitude/longitude so the API can use map center for ranking/radius.
 */
export const fetchSearchFromMapCenterWithLocation = (
	city,
	stateCode,
	latitude,
	longitude
) =>
	function fetchFromMapCenterWithLocation(dispatch, getState) {
		const reduxState = getState();
		const filterParams = selectSearchFilterParams(reduxState);
		const sortParam = selectSortParam(reduxState);
		const persistedQuery = reduxState.search.query || {};
		const persistedDistance =
			persistedQuery.distance || persistedQuery.maxDistance;
		const distanceParam = persistedDistance
			? { distance: persistedDistance }
			: {};

		const location = `${city}, ${stateCode}`;
		const query = {
			location,
			city,
			state: stateCode,
			page: 1,
			...sortParam,
			...filterParams,
			...distanceParam
		};
		if (
			typeof latitude === "number" &&
			typeof longitude === "number" &&
			Number.isFinite(latitude) &&
			Number.isFinite(longitude)
		) {
			query.latitude = latitude;
			query.longitude = longitude;
		}
		dispatch(getSearchPage({ query }));
	};

/**
 * Updates URL to filtered search page (same params as applySearch) and fetches from map center.
 * Use when user moves the map on /search/ so the URL persists and matches filter-click behavior.
 * @param {Object} payload.pathname - Current path (e.g. /search/)
 * @param {Object} payload.queryParams - Current URL query params
 * @param {Object} payload.resolved - { city, state } from reverse geocode
 * @param {Object} [payload.mapCenter] - { latitude, longitude } map center for API
 * @param {Function} payload.pushUrl - (url: string) => void, e.g. history.push
 */
export const pushSearchFromMapMove =
	({ pathname, queryParams, resolved, mapCenter, pushUrl }) =>
	(dispatch, getState) => {
		const state = getState();
		const params = buildFilteredSearchParams(state, queryParams, {
			location: `${resolved.city}, ${resolved.state}`,
			city: resolved.city,
			state: resolved.state,
			page: 1
		});
		delete params.pageType;
		pushUrl(`${buildSearchFilterLink(pathname, params)}#searchPage`);
		const lat = mapCenter?.latitude;
		const lng = mapCenter?.longitude;
		dispatch(
			fetchSearchFromMapCenterWithLocation(
				resolved.city,
				resolved.state,
				lat,
				lng
			)
		);
	};

/**
 * Map-drag search using only map center coordinates (no Geocode API).
 * Updates the search URL with location="<latitude>, <longitude>" and resets to page 1.
 * The existing URL-based flow (useEffect on search) will call GET /api/search with that location.
 * @param {Object} payload.pathname - Current path (e.g. /search/)
 * @param {Object} payload.queryParams - Current URL query params (filters, etc.)
 * @param {number} payload.latitude - Map center latitude
 * @param {number} payload.longitude - Map center longitude
 * @param {Function} payload.pushUrl - (url: string) => void, e.g. history.push
 */
export const pushSearchFromMapMoveByCoordinates =
	({ pathname, queryParams, latitude, longitude, pushUrl }) =>
	(dispatch, getState) => {
		const state = getState();
		const locationString = `${latitude}, ${longitude}`;
		const params = buildFilteredSearchParams(state, queryParams, {
			location: locationString,
			page: 1
		});
		delete params.pageType;
		// Omit city/state so URL and gateway use only coord-based location
		delete params.city;
		delete params.state;
		pushUrl(`${buildSearchFilterLink(pathname, params)}#searchPage`);
		// URL change will trigger SearchPageV2's useEffect and dispatch getSearchPage(query)
		// with location in params. No separate getSearchPage here to avoid duplicate request.
	};

export default searchSlice.reducer;
